import { useCallback, useState } from 'react';

import { useApolloClient } from '@apollo/react-hooks';
import { QueryOptions } from 'apollo-client';

import {
  GetINTLLoyaltyIdByEmail,
  GetINTLLoyaltyUserById,
  INTLLoyaltyIssuePoints,
  GetINTLLoyaltyTransactionsLegacy,
} from 'remote/queries';
import { IsoCountryCode2Char } from 'types';
import { INTLLoyaltyTransaction } from 'types/intl-loyalty';

export type INTLLoyaltyUser = {
  id: string;
  points: number;
  createdAt: string;
  email: string;
  name?: string;
  dateOfBirth?: string;
  cognitoId?: string;
};

export interface IINTLLoyaltyContext {
  loading: boolean;
  error: string;
  loyaltyMarket: IsoCountryCode2Char;
  setLoyaltyMarket: (market: IsoCountryCode2Char) => void;
  user?: INTLLoyaltyUser;
  userTransactions?: INTLLoyaltyTransaction[];
  fetchLoyaltyUserByEmail: (email: string) => Promise<INTLLoyaltyUser | undefined>;
  fetchLoyaltyUserById: (id: string) => Promise<INTLLoyaltyUser | undefined>;
  fetchLoyaltyTransactions: (id: string) => Promise<INTLLoyaltyTransaction[]>;
  issuePoints: (
    loyaltyId: string,
    loyaltyUserCognitoId: string,
    points: number,
    reason: string,
    orderId?: string,
  ) => Promise<number | undefined>;
}

type QueryClientOptions = Pick<QueryOptions, 'query' | 'variables' | 'fetchPolicy' | 'context'> & {
  path: string;
  errorMsg: string;
};

const useINTLLoyalty = (): IINTLLoyaltyContext => {
  const client = useApolloClient();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [user, setUser] = useState<INTLLoyaltyUser | undefined>();
  const [userTransactions, setUserTransactions] = useState<INTLLoyaltyTransaction[]>([]);
  const [loyaltyMarket, setMarket] = useState<IsoCountryCode2Char>(IsoCountryCode2Char.US);

  const queryClient = useCallback(
    async <T>({
      query,
      variables,
      errorMsg,
      path,
      fetchPolicy = 'cache-first',
    }: QueryClientOptions): Promise<T | undefined> => {
      setLoading(true);
      let result: T | undefined;
      try {
        const { data, errors } = await client.query({
          query,
          variables,
          errorPolicy: 'all',
          fetchPolicy,
          context: {
            headers: {
              'x-region': loyaltyMarket,
            },
          },
        });
        result = data && data[path];
        if (!result || errors) {
          setError(errorMsg);
        } else {
          setError('');
        }
      } catch (err) {
        setError(errorMsg);
      }
      setLoading(false);
      return result;
    },
    [client, loyaltyMarket],
  );

  const fetchLoyaltyUserById = useCallback(
    async (loyaltyId: string) => {
      const result = await queryClient<INTLLoyaltyUser>({
        query: GetINTLLoyaltyUserById,
        variables: { loyaltyId },
        errorMsg: 'Failed to fetch loyalty user',
        path: 'intlLoyaltyUserById',
        fetchPolicy: 'network-only',
      });
      setUser(result);
      return result;
    },
    [queryClient],
  );

  const fetchLoyaltyUserByEmail = useCallback(
    async (email: string) => {
      const loyaltyId = await queryClient<string>({
        query: GetINTLLoyaltyIdByEmail,
        variables: { email },
        errorMsg: 'No loyalty account found for email',
        path: 'intlLoyaltyIdByEmail',
      });
      if (loyaltyId) {
        return fetchLoyaltyUserById(loyaltyId);
      }
    },
    [queryClient, fetchLoyaltyUserById],
  );

  const fetchLoyaltyTransactions = useCallback(
    async (loyaltyId: string) => {
      const results = await queryClient<INTLLoyaltyTransaction[]>({
        query: GetINTLLoyaltyTransactionsLegacy,
        variables: { loyaltyId },
        errorMsg: 'Failed to fetch loyalty user transactions',
        path: 'intlLoyaltyTransactions',
      });
      const transactions = results || [];
      setUserTransactions(transactions);
      return transactions;
    },
    [queryClient],
  );

  const issuePoints = useCallback(
    async (
      loyaltyId: string,
      loyaltyUserCognitoId: string,
      points: number,
      reason: string,
      orderId?: string,
    ) => {
      const { data } = await client.mutate({
        mutation: INTLLoyaltyIssuePoints,
        variables: { loyaltyId, loyaltyUserCognitoId, points, reason, orderId },
      });
      const dataPath = 'intlLoyaltyIssuePoints';
      const result: number | undefined = (data && data[dataPath]) || undefined;
      if (result && user) {
        const newPoints = user.points + result;
        setUser({
          ...user,
          points: newPoints,
        });
        return newPoints;
      }
    },
    [client, user],
  );

  const setLoyaltyMarket = (market: IsoCountryCode2Char) => setMarket(market);

  return {
    loyaltyMarket,
    setLoyaltyMarket,
    loading,
    error,
    user,
    userTransactions,
    issuePoints,
    fetchLoyaltyUserByEmail,
    fetchLoyaltyUserById,
    fetchLoyaltyTransactions,
  };
};

export default useINTLLoyalty;
